import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/lib/ws";
import { useRideChat } from "@/hooks/useRideChat";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import MapView, { driverIcon, pickupIcon, destIcon } from "@/components/MapView";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { RideChat } from "@/components/RideChat";
import { RatingModal } from "@/components/RatingModal";
import { EmergencyContactsModal } from "@/components/EmergencyContactsModal";
import { PassengerHistorySheet } from "@/components/passenger/PassengerHistorySheet";
import { MyDataCard } from "@/components/driver/AccountPanel";
import { BottomNav } from "@/components/BottomNav";
import { Marker, Polyline } from "react-leaflet";
import { Taxi, SignOut, ClockCounterClockwise, MapPin, X, Star, Clock, CurrencyEur, CaretLeft, ShieldWarning, WarningOctagon, CrosshairSimple, ChatCircleDots, Money, CreditCard, MapTrifold, UserCircle } from "@phosphor-icons/react";

const DEFAULT_CENTER = [40.4168, -3.7038]; // Madrid
const ROUTE_STYLE = { color: "#F5B700", weight: 5, opacity: 0.85 };
const REQUEST_TIMEOUT = 30;

const STATUS_LABELS = {
  pending: { className: "pill-warn", text: "Esperando respuesta..." },
  accepted: { className: "pill-info", text: "Conductor en camino" },
  in_progress: { className: "pill-online", text: "En viaje" },
  completed: { className: "pill-offline", text: "Completado" },
};

// Nocturno 21:00–06:00 según la hora local del pasajero
function isNightNow() {
  const h = new Date().getHours();
  return h >= 21 || h < 6;
}

function getRidePath(ride) {
  if (!ride) return [];
  if (ride.route_geometry?.length) return ride.route_geometry;
  return [[ride.pickup_lat, ride.pickup_lng], [ride.dest_lat, ride.dest_lng]];
}

export default function PassengerDashboard() {
  const { user, logout, refresh } = useAuth();
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [drivers, setDrivers] = useState([]);
  const [taxis, setTaxis] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [driverPos, setDriverPos] = useState(null);
  const [showRequest, setShowRequest] = useState(false);
  const [step, setStep] = useState("form"); // form | list
  const [section, setSection] = useState("home"); // home | history | security | profile
  const [history, setHistory] = useState([]);
  const [pickupCoords, setPickupCoords] = useState(DEFAULT_CENTER);
  const [pickupAddress, setPickupAddress] = useState("Mi ubicación");
  const [destCoords, setDestCoords] = useState(null);
  const [route, setRoute] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash"); // 'cash' | 'card'
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [waitLeft, setWaitLeft] = useState(REQUEST_TIMEOUT);
  const waitTimerRef = useRef(null);
  // Emergency contacts
  const [contacts, setContacts] = useState([]);
  // Chat & rating
  const [ratingRide, setRatingRide] = useState(null);
  const { messages, showChat, setShowChat, unread, openChat, handleChatMessage, resetChat } =
    useRideChat({ activeRide, userId: user?.id });

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 4500); };

  const refreshActive = useCallback(async () => {
    const { data } = await api.get("/rides/active");
    setActiveRide(data || null);
  }, []);

  const refreshDrivers = useCallback(async (c) => {
    const at = c || center;
    try {
      const { data } = await api.get("/drivers/nearby", { params: { lat: at[0], lng: at[1] } });
      setDrivers(data || []);
    } catch (err) { logger.warn("[passenger] nearby drivers fetch failed", err); }
  }, [center]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { const c = [p.coords.latitude, p.coords.longitude]; setCenter(c); setPickupCoords(c); },
        () => {}
      );
    }
  }, []);

  useEffect(() => {
    refreshActive();
    refreshDrivers();
    const t = setInterval(() => refreshDrivers(), 10000);
    return () => clearInterval(t);
  }, [refreshActive, refreshDrivers]);

  useEffect(() => {
    if (activeRide?.status === "pending") {
      setWaitLeft(REQUEST_TIMEOUT);
      clearInterval(waitTimerRef.current);
      waitTimerRef.current = setInterval(() => setWaitLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
      return () => clearInterval(waitTimerRef.current);
    }
    clearInterval(waitTimerRef.current);
  }, [activeRide?.status, activeRide?.id]);

  const reopenList = useCallback(async () => {
    if (destCoords) {
      try {
        const { data } = await api.get("/drivers/nearby", {
          params: { lat: pickupCoords[0], lng: pickupCoords[1], dest_lat: destCoords.lat, dest_lng: destCoords.lng, distance_km: route?.distance_km, night: isNightNow() },
        });
        setTaxis(data || []);
      } catch (err) { logger.error("[passenger] reopen list failed", err); }
    }
    setShowRequest(true);
    setStep("list");
  }, [destCoords, pickupCoords, route]);

  const handleWs = useCallback((msg) => {
    if (msg.type === "ride_accepted" || msg.type === "ride_started" || msg.type === "ride_completed") {
      setActiveRide(msg.ride);
      if (msg.type === "ride_completed") { setRatingRide(msg.ride); resetChat(); setTimeout(() => setActiveRide(null), 5000); }
    } else if (msg.type === "ride_cancelled") {
      setActiveRide(null); setDriverPos(null); resetChat();
    } else if (msg.type === "ride_rejected") {
      setActiveRide(null); setDriverPos(null);
      flash(msg.reason === "timeout" ? "El conductor no respondió a tiempo. Elige otro taxi." : "El conductor rechazó tu solicitud. Elige otro taxi.");
      reopenList();
    } else if (msg.type === "driver_location") {
      setDriverPos({ lat: msg.lat, lng: msg.lng });
    } else if (msg.type === "chat") {
      handleChatMessage(msg);
    }
  }, [reopenList, handleChatMessage, resetChat]);

  const { connected, send } = useWebSocket(handleWs, !!user);

  const sendChat = (text) => send({ type: "chat", ride_id: activeRide.id, text });

  const submitRating = async (rating, comment) => {
    try {
      await api.post("/rides/rate", { ride_id: ratingRide.id, rating, comment });
      flash("¡Gracias por tu calificación!");
    } catch (err) {
      flash(err?.response?.data?.detail || "No se pudo enviar la calificación.");
    } finally {
      setRatingRide(null);
    }
  };

  const searchTaxis = async () => {
    if (!destCoords) { flash("Elige un destino de la lista de sugerencias."); return; }
    setBusy(true);
    try {
      const { data: r } = await api.get("/geo/route", {
        params: { from_lat: pickupCoords[0], from_lng: pickupCoords[1], to_lat: destCoords.lat, to_lng: destCoords.lng },
      });
      setRoute(r);
      const { data } = await api.get("/drivers/nearby", {
        params: { lat: pickupCoords[0], lng: pickupCoords[1], dest_lat: destCoords.lat, dest_lng: destCoords.lng, distance_km: r.distance_km, night: isNightNow() },
      });
      setTaxis(data || []);
      setStep("list");
    } finally { setBusy(false); }
  };

  const requestTaxi = async (taxi) => {
    if (!destCoords) return;
    setBusy(true);
    try {
      const { data } = await api.post("/rides/request", {
        driver_id: taxi.id,
        pickup_lat: pickupCoords[0], pickup_lng: pickupCoords[1], pickup_address: pickupAddress,
        dest_lat: destCoords.lat, dest_lng: destCoords.lng, dest_address: destCoords.address,
        estimated_price: taxi.fare,
        distance_km: route?.distance_km,
        night: isNightNow(),
        payment_method: paymentMethod,
        route_geometry: route?.geometry,
      });
      setActiveRide(data);
      setShowRequest(false);
      setStep("form");
    } catch (err) {
      flash(err?.response?.data?.detail || "No se pudo solicitar el taxi.");
      reopenList();
    } finally { setBusy(false); }
  };

  const cancelRide = async () => {
    if (!activeRide) return;
    await api.post(`/rides/${activeRide.id}/cancel`);
    setActiveRide(null); setDriverPos(null);
  };

  const loadHistory = async () => {
    const { data } = await api.get("/rides/history");
    setHistory(data || []);
  };

  // ----- Emergency contacts -----
  const loadContacts = async () => {
    const { data } = await api.get("/contacts");
    setContacts(data || []);
  };
  const addContact = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = { name: fd.get("name"), phone: fd.get("phone"), email: fd.get("email") || null };
    const { data } = await api.post("/contacts", payload);
    setContacts((c) => [data, ...c]);
    e.target.reset();
  };
  const deleteContact = async (id) => {
    await api.delete(`/contacts/${id}`);
    setContacts((c) => c.filter((x) => x.id !== id));
  };

  // ----- Panic -----
  const triggerPanic = () => {
    if (!window.confirm("¿Enviar alerta de emergencia al administrador y a tus contactos?")) return;
    const send = (lat, lng) =>
      api.post("/panic", { ride_id: activeRide?.id || null, lat, lng })
        .then(() => flash("Alerta de emergencia enviada. Ayuda en camino."))
        .catch(() => flash("No se pudo enviar la alerta."));
    if (navigator.geolocation) navigator.geolocation.getCurrentPosition((p) => send(p.coords.latitude, p.coords.longitude), () => send(center[0], center[1]));
    else send(center[0], center[1]);
  };

  const markers = useMemo(
    () => (taxis.length ? taxis : drivers).map((d) => ({
      ...d,
      icon: driverIcon,
      popup: `${d.name} · ${d.vehicle_model || "Vehículo"}${d.vehicle_color ? " (" + d.vehicle_color + ")" : ""}`,
    })),
    [taxis, drivers]
  );
  const rideActive = activeRide && (activeRide.status === "accepted" || activeRide.status === "in_progress");
  const ridePath = getRidePath(activeRide);

  // Load section data when entering a tab
  useEffect(() => {
    if (section === "history") loadHistory();
    else if (section === "security") loadContacts();
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const passengerTabs = [
    { id: "home", label: "Inicio", icon: MapTrifold, testId: "passenger-nav-home" },
    { id: "history", label: "Viajes", icon: ClockCounterClockwise, testId: "passenger-nav-history" },
    { id: "security", label: "Seguridad", icon: ShieldWarning, testId: "passenger-nav-security" },
    { id: "profile", label: "Perfil", icon: UserCircle, testId: "passenger-nav-profile" },
  ];

  return (
    <div className="app-dark app-shell relative h-screen overflow-hidden" data-testid="passenger-app">
      <div className="absolute inset-0">
        <MapView center={center} zoom={14} markers={markers} recenter>
          {activeRide && (
            <>
              <Marker position={[activeRide.pickup_lat, activeRide.pickup_lng]} icon={pickupIcon} />
              <Marker position={[activeRide.dest_lat, activeRide.dest_lng]} icon={destIcon} />
              <Polyline positions={ridePath} pathOptions={ROUTE_STYLE} />
            </>
          )}
          {driverPos && <Marker position={[driverPos.lat, driverPos.lng]} icon={driverIcon} />}
        </MapView>
      </div>

      {section === "home" && (
      <>
      {notice && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1200] glass rounded-full px-5 py-2.5 text-sm font-semibold shadow-lg fade-in" data-testid="passenger-notice">
          {notice}
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-4 left-4 right-4 z-50 flex items-center justify-between" data-testid="passenger-topbar">
        <div className="glass rounded-full px-4 py-2 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Taxi size={18} color="#000" weight="duotone" />
          </div>
          <div>
            <div className="text-xs text-slate-500">Hola,</div>
            <div className="font-bold text-sm">{user?.name}</div>
          </div>
          <span className={`pill ${connected ? "pill-online" : "pill-offline"}`}>
            {connected && <span className="dot-pulse" />} {connected ? "En línea" : "Sin conexión"}
          </span>
        </div>
      </div>

      {/* Bottom sheet: request flow */}
      {!activeRide && (
        <div className="bottom-sheet slide-up" data-testid="passenger-bottom-sheet">
          <div className="handle" />
          {!showRequest && (
            <>
              <h2 className="text-2xl font-display font-bold mb-2">¿A dónde vamos?</h2>
              <p className="text-slate-500 text-sm mb-3">{drivers.length} {drivers.length === 1 ? "conductor cerca" : "conductores cerca de ti"}</p>
              {drivers.length > 0 && (
                <div className="space-y-2 mb-4 max-h-[34vh] overflow-auto scroll-area" data-testid="nearby-taxis-preview">
                  {drivers.map((d) => (
                    <div key={d.id} className="card flex items-center gap-3 py-2.5" data-testid={`nearby-taxi-${d.id}`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--primary)" }}>
                        <Taxi size={20} color="white" weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm truncate">{d.name}</span>
                          <span className="flex items-center gap-0.5 text-xs text-slate-500"><Star size={12} weight="fill" className="text-amber-400" />{Number(d.rating).toFixed(1)}</span>
                        </div>
                        <div className="text-xs text-slate-500 truncate" data-testid={`nearby-taxi-vehicle-${d.id}`}>
                          {d.vehicle_model}{d.vehicle_color ? ` · ${d.vehicle_color}` : ""}{d.vehicle_plate ? ` · ${d.vehicle_plate}` : ""}
                        </div>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-slate-600 shrink-0"><Clock size={13} weight="duotone" />{d.eta_minutes} min</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 mb-2">Indica tu destino para ver el precio de cada conductor.</p>
              <button onClick={() => { setShowRequest(true); setStep("form"); }} className="btn-primary w-full text-lg" data-testid="open-request-btn">
                <MapPin size={20} className="inline mr-2" weight="bold" /> Pedir taxi
              </button>
            </>
          )}

          {showRequest && step === "form" && (
            <div className="space-y-3 fade-in">
              <h2 className="text-2xl font-display font-bold mb-1">Tu viaje</h2>
              <div className="relative">
                <AddressAutocomplete
                  label="Recogida"
                  initialValue={pickupAddress}
                  placeholder="Dirección de recogida..."
                  testid="pickup-input"
                  onSelect={(sel, raw) => {
                    if (sel) { setPickupCoords([sel.lat, sel.lng]); setPickupAddress(sel.address); }
                    else setPickupAddress(raw);
                  }}
                />
                <button
                  type="button"
                  onClick={() => { setPickupCoords(center); setPickupAddress("Mi ubicación"); flash("Recogida fijada en tu ubicación actual."); }}
                  className="text-xs text-amber-600 font-semibold mt-1 flex items-center gap-1"
                  data-testid="use-my-location-btn"
                >
                  <CrosshairSimple size={14} weight="bold" /> Usar mi ubicación
                </button>
              </div>
              <AddressAutocomplete
                label="Destino"
                placeholder="¿A dónde vas?"
                testid="dest-input"
                onSelect={(sel) => setDestCoords(sel)}
              />
              <div className="flex gap-2 pt-1">
                <button onClick={() => setShowRequest(false)} className="btn-ghost flex-1">Cancelar</button>
                <button onClick={searchTaxis} disabled={busy || !destCoords} className="btn-primary flex-1" data-testid="search-taxis-btn">
                  {busy ? "Buscando..." : "Buscar taxis"}
                </button>
              </div>
            </div>
          )}

          {showRequest && step === "list" && (
            <div className="fade-in" style={{ maxHeight: "60vh", overflow: "auto" }}>
              <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setStep("form")} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center" data-testid="back-to-form-btn">
                  <CaretLeft size={18} />
                </button>
                <h2 className="text-xl font-display font-bold">Taxis disponibles</h2>
              </div>
              {route && (
                <p className="text-slate-500 text-sm mb-3">
                  {route.distance_km} km · ~{route.duration_min} min · <span className="text-slate-400">precio según conductor{isNightNow() ? " · tarifa nocturna" : ""}</span>
                </p>
              )}
              <div className="mb-3" data-testid="payment-method-selector">
                <span className="label">Forma de pago</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("cash")}
                    className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold border transition-colors ${paymentMethod === "cash" ? "border-transparent text-slate-900" : "border-slate-200 text-slate-500 bg-white"}`}
                    style={paymentMethod === "cash" ? { background: "var(--primary-soft)", borderColor: "var(--primary)" } : {}}
                    data-testid="payment-cash-btn"
                  >
                    <Money size={18} weight="duotone" /> Efectivo
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("card")}
                    className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold border transition-colors ${paymentMethod === "card" ? "border-transparent text-slate-900" : "border-slate-200 text-slate-500 bg-white"}`}
                    style={paymentMethod === "card" ? { background: "var(--primary-soft)", borderColor: "var(--primary)" } : {}}
                    data-testid="payment-card-btn"
                  >
                    <CreditCard size={18} weight="duotone" /> Tarjeta
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">El conductor te cobrará directamente con el método elegido.</p>
              </div>
              <div className="space-y-3 scroll-area">
                {taxis.length === 0 && (
                  <p className="text-slate-500 text-center py-8" data-testid="no-taxis-msg">No hay taxis disponibles cerca. Intenta más tarde.</p>
                )}
                {taxis.map((t) => (
                  <div key={t.id} className="card flex items-center gap-3" data-testid={`taxi-option-${t.id}`}>
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--primary)" }}>
                      <Taxi size={24} color="white" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold truncate">{t.name}</span>
                        <span className="flex items-center gap-0.5 text-xs text-slate-500"><Star size={12} weight="fill" className="text-amber-400" />{Number(t.rating).toFixed(1)}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{t.vehicle_model}{t.vehicle_color ? ` · ${t.vehicle_color}` : ""}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Bandera €{t.base_fee?.toFixed(2)} + €{t.rate_per_km?.toFixed(2)}/km</div>
                      <div className="flex items-center gap-3 mt-1 text-sm">
                        <span className="flex items-center gap-1 text-slate-700"><Clock size={14} weight="duotone" />{t.eta_minutes} min</span>
                        <span className="flex items-center gap-1 font-bold" style={{ color: "var(--primary)" }} data-testid={`taxi-fare-${t.id}`}><CurrencyEur size={14} weight="duotone" />{t.fare?.toFixed(2)}</span>
                      </div>
                    </div>
                    <button onClick={() => requestTaxi(t)} disabled={busy} className="btn-primary px-4 py-2 text-sm shrink-0" data-testid={`request-taxi-btn-${t.id}`}>
                      Pedir
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active / waiting ride sheet */}
      {activeRide && (
        <div className="bottom-sheet slide-up" data-testid="active-ride-sheet">
          <div className="handle" />
          <div className="flex items-center justify-between mb-3">
            {(() => {
              const s = STATUS_LABELS[activeRide.status] || STATUS_LABELS.completed;
              return <span className={`pill ${s.className}`}>{s.text}</span>;
            })()}
            <span className="font-display font-bold text-xl" style={{ color: "var(--primary)" }}>
              €{activeRide.estimated_price?.toFixed(2)}
            </span>
          </div>

          {activeRide.status === "pending" && (
            <div className="text-center py-4" data-testid="waiting-driver">
              <div className="text-5xl font-display font-bold mb-1" style={{ color: "var(--primary)" }}>{waitLeft}s</div>
              <p className="text-slate-500 text-sm">Esperando que el conductor acepte...</p>
            </div>
          )}

          <div className="space-y-2 mb-4 text-sm">
            <div className="flex gap-2 items-start"><div className="w-3 h-3 mt-1 rounded-full bg-emerald-500" /> <span>{activeRide.pickup_address}</span></div>
            <div className="flex gap-2 items-start"><div className="w-3 h-3 mt-1 rounded-full bg-red-500" /> <span>{activeRide.dest_address}</span></div>
            {activeRide.payment_method && (
              <div className="flex gap-2 items-center text-slate-600" data-testid="passenger-payment-method">
                {activeRide.payment_method === "card" ? <CreditCard size={16} weight="duotone" style={{ color: "var(--primary)" }} /> : <Money size={16} weight="duotone" style={{ color: "var(--primary)" }} />}
                <span>Pago: <strong>{activeRide.payment_method === "card" ? "Tarjeta" : "Efectivo"}</strong></span>
              </div>
            )}
          </div>
          {activeRide.driver_name && (
            <div className="bg-slate-50 rounded-xl p-3 mb-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">Conductor</div>
                <div className="font-bold">{activeRide.driver_name}</div>
              </div>
              <Taxi size={28} weight="duotone" style={{ color: "var(--primary)" }} />
            </div>
          )}
          {(rideActive || activeRide.status === "pending" || activeRide.status === "accepted") && (
            <div className="sheet-sticky-actions" data-testid="ride-actions">
              {rideActive && (
                <div className="flex gap-2 mb-2">
                  {activeRide.driver_name && (
                    <button onClick={openChat} className="btn-ghost flex-1 relative flex items-center justify-center gap-2" data-testid="open-chat-btn">
                      <ChatCircleDots size={18} weight="duotone" style={{ color: "var(--primary)" }} /> Mensaje
                      {unread > 0 && <span className="bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="chat-unread-badge">{unread}</span>}
                    </button>
                  )}
                  <button onClick={triggerPanic} className="btn-ghost flex-1 flex items-center justify-center gap-2 text-white font-bold" style={{ background: "#DC2626" }} data-testid="panic-button">
                    <WarningOctagon size={18} weight="fill" /> SOS
                  </button>
                </div>
              )}
              {(activeRide.status === "pending" || activeRide.status === "accepted") && (
                <button onClick={cancelRide} className="btn-ghost w-full text-red-600" data-testid="cancel-ride-btn">
                  <X size={16} className="inline mr-1" /> Cancelar viaje
                </button>
              )}
            </div>
          )}
        </div>
      )}

      </>
      )}

      {/* Tab sections */}
      {section === "history" && (
        <PassengerHistorySheet embedded history={history} onRate={(r) => setRatingRide(r)} />
      )}
      {section === "security" && (
        <EmergencyContactsModal embedded contacts={contacts} onAdd={addContact} onDelete={deleteContact} />
      )}
      {section === "profile" && (
        <div className="app-section" data-testid="passenger-profile-section">
          <h2 className="section-title font-display">Mi perfil</h2>
          <div className="card mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "var(--primary)" }}>
                <Taxi size={26} color="#000" weight="duotone" />
              </div>
              <div>
                <div className="font-bold text-lg">{user?.name}</div>
                <div className="text-sm text-slate-500">{user?.email}</div>
              </div>
            </div>
          </div>
          <MyDataCard user={user} onSaved={refresh} />
          <button onClick={logout} className="btn-ghost w-full text-red-500 mt-4" data-testid="passenger-logout-btn">
            <SignOut size={18} className="inline mr-2" weight="duotone" /> Cerrar sesión
          </button>
        </div>
      )}

      {/* Ride chat (contextual) */}
      {showChat && activeRide && (
        <RideChat
          messages={messages}
          myId={user?.id}
          otherName={activeRide.driver_name}
          onSend={sendChat}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Rating modal */}
      {ratingRide && (
        <RatingModal ride={ratingRide} onSubmit={submitRating} onClose={() => setRatingRide(null)} />
      )}

      <BottomNav tabs={passengerTabs} active={section} onChange={setSection} />
    </div>
  );
}
